import { add, multiply, portion, timedRate } from "./economy-math.js";
import { buildingRule, EMERGENCY_WORKSHOP, outputRule } from "./economy-rules.js";
import type { BuildingCapacities, EconomyInput, PowerDepartment } from "./economy-types.js";
import { formatMicro, type Micro, microFromString } from "./exact-numbers.js";
import { effectMultiplier } from "./research-effects.js";

export interface RecipeOutput {
  readonly output: keyof BuildingCapacities;
  readonly capacity: Micro;
  readonly metalPerCapacity: string;
}

export interface ProductionCapture {
  readonly mined: Micro;
  readonly constructionWork: Micro;
  readonly assemblyWork: Micro;
  readonly research: Micro;
  readonly shieldRecharge: Micro;
  readonly intended: readonly { readonly recipe: RecipeOutput; readonly output: Micro }[];
}

type Operation = Readonly<Record<PowerDepartment, readonly [Micro, Micro]>>;

export function captureProduction(input: EconomyInput, operation: Operation): ProductionCapture {
  const industry = operation.industry;
  const constructionActive =
    (input.manufacturing.newConstruction &&
      input.manufacturing.constructionSplit.newConstruction > 0) ||
    (input.manufacturing.restoration && input.manufacturing.constructionSplit.restoration > 0);
  const constructionRate = add(
    whole(EMERGENCY_WORKSHOP.outputs.constructionWorkPerSecond),
    operate(
      multiply(
        constructionActive ? input.buildings["construction-yard"] : (0n as Micro),
        multipliedRate(input, "construction-yard", "construction-work", "construction-yard-output"),
      ),
      industry,
    ),
  );
  const assemblyRate = operate(
    multiply(
      input.manufacturing.assembly ? input.buildings["robot-factory"] : (0n as Micro),
      multipliedRate(input, "robot-factory", "assembly-work", "assembly-work"),
    ),
    operation["military-production"],
  );
  const mined = timedRate(
    add(
      whole(EMERGENCY_WORKSHOP.outputs.metalPerSecond),
      operate(
        multiply(
          input.buildings["mining-rig"],
          multipliedRate(input, "mining-rig", "metal", "mining-rig-output"),
        ),
        industry,
      ),
    ),
    input.elapsedMs,
  );
  const constructionWork = timedRate(constructionRate, input.elapsedMs);
  const assemblyWork = timedRate(assemblyRate, input.elapsedMs);
  const research = timedOutput(
    input,
    "research-lab",
    "research-points",
    "research-lab-output",
    operation.research,
  );
  const shieldRecharge = timedOutput(
    input,
    "shield-generator",
    "shield-charge",
    "shield-recharge",
    operation.defenses,
  );
  const intended = producerRecipes(input, industry).map((recipe) => ({
    recipe,
    output: timedRate(recipe.capacity, input.elapsedMs),
  }));
  return { mined, constructionWork, assemblyWork, research, shieldRecharge, intended };
}

function producerRecipes(
  input: EconomyInput,
  operation: readonly [Micro, Micro],
): readonly RecipeOutput[] {
  const b = input.buildings;
  const recipes: RecipeOutput[] = [
    recipe(input, "rig-factory", "mining-rig", b["rig-factory"], operation),
    recipe(input, "yard-factory", "construction-yard", b["yard-factory"], operation),
  ];
  for (const output of ["rig-factory", "yard-factory", "robot-factory"] as const) {
    const split = input.manufacturing.factoryWorksOperating
      ? portion(b["factory-works"], BigInt(input.factoryWorksSplit[output]), 100n)
      : (0n as Micro);
    recipes.push(recipe(input, "factory-works", output, split, operation));
  }
  return recipes;
}

function recipe(
  input: EconomyInput,
  producer: Parameters<typeof outputRule>[0],
  output: keyof BuildingCapacities,
  capacity: Micro,
  operation: readonly [Micro, Micro],
): RecipeOutput {
  return {
    output,
    capacity: operate(
      multiply(capacity, multipliedRate(input, producer, output, "producer-factory-output")),
      operation,
    ),
    metalPerCapacity: String(buildingRule(output).metalCost),
  };
}

function timedOutput(
  input: EconomyInput,
  producer: "research-lab" | "shield-generator",
  quantity: "research-points" | "shield-charge",
  modifier: string,
  operation: readonly [Micro, Micro],
): Micro {
  return timedRate(
    operate(
      multiply(input.buildings[producer], multipliedRate(input, producer, quantity, modifier)),
      operation,
    ),
    input.elapsedMs,
  );
}

function multipliedRate(
  input: EconomyInput,
  producer: Parameters<typeof outputRule>[0],
  quantity: string,
  modifier: string,
): string {
  const base = outputRule(producer, quantity).perSecond;
  return formatMicro(
    multiply(microFromString(base), effectMultiplier(input.researchEffects, modifier)),
  );
}

function operate(value: Micro, operation: readonly [Micro, Micro]): Micro {
  return operation[1] === 0n ? (0n as Micro) : (((value * operation[0]) / operation[1]) as Micro);
}

function whole(value: number): Micro {
  return (BigInt(value) * 1_000_000n) as Micro;
}
